import React, { useReducer, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'

import Button from '@aytm/lib/rfd-ui/components/basic/button'

import { commentStatusesReducer, pageDataReducer } from '@aytm/app/stats/page/comments/reducers'
import {
  DEFAULT_COMMENT_STATUSES_STATE,
  DEFAULT_QUERY,
  DEFAULT_ORDER_BY,
  DEFAULT_ORDER_DIRECTION,
  DEFAULT_PER_PAGE,
  DEFAULT_PER_PAGE_IMAGE_RESPONSES,
  DEFAULT_PER_PAGE_VIDEO_RESPONSES,
  DEFAULT_SEARCH_STATE,
  DEFAULT_SELECTED_QUESTION
} from '@aytm/app/stats/page/comments/lib/constants'
import { mapReportSearchQuery } from '@aytm/app/stats/page/comments/lib/utils'
import { setApiKey } from '@aytm/app/stats/page/comments/apiCalls'

export const OEContext = React.createContext()

import CommentsOpenEnded from '@aytm/app/stats/page/comments/comments'
import ImageResponses from '@aytm/app/stats/page/comments/image-responses'
import VideoResponses from '@aytm/app/stats/page/comments/video-responses'

// styles
import style from '@aytm/app/stats/page/comments/styles/index.module.scss'

const Comments = props => {
  const {
    specialMode = 'comments',
    imagesData,
    CSRF,
    apiKey,
    reportParams,
    translation,
    sentimentOptions,
    isAdmin,
    isSurveyOwner,
    isSurveyGuest,
    isRejectAvailable,
    isShowAllTools,
    isQAModeDisabled,
    isTagsManagementDisabled,
    isSentimentsManagementDisabled,
    isShowCorrectionsDisabled,
    ...rest
  } = props

  // set API endpoints
  setApiKey(apiKey)

  const components = {
    comments: CommentsOpenEnded,
    'image-responses': ImageResponses,
    'video-responses': VideoResponses
  }

  const [searchState, dispatch] = useReducer(pageDataReducer, {
    ...DEFAULT_SEARCH_STATE,
    CSRF,
    sentimentOptions,
    request: {
      ...DEFAULT_SEARCH_STATE.request,
      // derive the query the same way the request re-derivation does -- a first build that disagrees
      // with the re-derivation is committed as a change moments later, and every change costs a fetch
      query: mapReportSearchQuery(reportParams.query || DEFAULT_QUERY),
      selectedQuestion: reportParams.selectedQuestion || DEFAULT_SELECTED_QUESTION,
      pageSize:
        reportParams.pageSize ||
        ('image-responses' == specialMode
          ? DEFAULT_PER_PAGE_IMAGE_RESPONSES
          : 'video-responses' == specialMode
            ? DEFAULT_PER_PAGE_VIDEO_RESPONSES
            : DEFAULT_PER_PAGE),
      orderBy: reportParams.orderBy || DEFAULT_ORDER_BY,
      orderDirection: reportParams.orderDirection || DEFAULT_ORDER_DIRECTION
    },
    isAdmin,
    isSurveyOwner,
    isSurveyGuest,
    isRejectAvailable,
    isShowAllTools: isShowAllTools || false,
    isShowAllTraits: false,
    isQAModeDisabled,
    isTagsManagementDisabled,
    isSentimentsManagementDisabled,
    isShowCorrectionsDisabled,
    isShowSentiments: !isSentimentsManagementDisabled,
    isVideoResponsesMode: 'video-responses' == specialMode
  })
  const [commentStatusesState, dispatchCommentStatuses] = useReducer(
    commentStatusesReducer,
    DEFAULT_COMMENT_STATUSES_STATE
  )

  // Bound the per-response status store: when the page set changes, drop entries
  // for respondents no longer present (see _open-questions.md §OQ-10).
  useEffect(() => {
    dispatchCommentStatuses({
      type: 'PRUNE',
      data: { uids: (searchState.pageData || []).map(item => item?.response?.uid).filter(uid => uid != undefined) }
    })
  }, [searchState.pageData])

  // emptying the errors list remounts the mode subtree, whose fetch effect then re-runs
  const onRetry = useCallback(() => dispatch({ type: 'SEARCH_INIT' }), [dispatch])

  const oeContextValue = useMemo(() => {
    return { imagesData, searchState, translation, dispatch, commentStatusesState, dispatchCommentStatuses }
  }, [imagesData, searchState, translation, dispatch, commentStatusesState, dispatchCommentStatuses])

  const Component = components[specialMode]

  return (
    <div className={style.component}>
      {searchState.errors.length ? (
        <div className={style['comments-errors']}>
          <h3>Error</h3>
          {searchState.errors.map(error => (
            <div key={`error${error.name}`}>{error.message}</div>
          ))}
          {/* the error block replaces the whole subtree, so it needs its own way out */}
          <div className={style['comments-errors-retry']}>
            <Button label="RETRY" onClick={onRetry} />
          </div>
        </div>
      ) : (
        <OEContext.Provider value={oeContextValue}>
          <Component
            apiKey={apiKey}
            specialMode={specialMode}
            reportParams={reportParams}
            sentimentOptions={sentimentOptions}
            {...rest}
          />
        </OEContext.Provider>
      )}
    </div>
  )
}

Comments.propTypes = {
  specialMode: PropTypes.string,
  surveyData: PropTypes.object,
  imagesData: PropTypes.object,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  apiKey: PropTypes.object,
  CSRF: PropTypes.string,
  questionId: PropTypes.string,
  loadRejectedCS: PropTypes.string,
  reportParams: PropTypes.object,
  translation: PropTypes.string,
  sentimentOptions: PropTypes.object,
  reportVirtual: PropTypes.object,
  reports: PropTypes.array,
  reportSchema: PropTypes.array,
  isAdmin: PropTypes.bool,
  isSurveyOwner: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  isRejectAvailable: PropTypes.bool,
  isShowAllTools: PropTypes.bool,
  isShowHiddenFilters: PropTypes.bool,
  isFilterDuplicates: PropTypes.bool,
  isAdvancedFiltersDisabled: PropTypes.bool,
  isQAModeDisabled: PropTypes.bool,
  isTagsManagementDisabled: PropTypes.bool,
  isSentimentsManagementDisabled: PropTypes.bool,
  isShowCorrectionsDisabled: PropTypes.bool,
  isAiAssistantsOECodingDisabled: PropTypes.bool,
  isSpecialsSortingDisabled: PropTypes.bool,
  onTagsLoad: PropTypes.func,
  onTagsFetch: PropTypes.func,
  onFetch: PropTypes.func,
  onFetchSchema: PropTypes.func,
  onFetchApiKey: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onUpdateSchema: PropTypes.func,
  onCreateFilter: PropTypes.func,
  onTranslationChange: PropTypes.func,
  onQuestionsListChange: PropTypes.func,
  onSelectedQuestionChange: PropTypes.func,
  onQAModeChange: PropTypes.func
}

Comments.displayName = 'Comments'

export default Comments
